/**
 * `buildSystemLayout(galaxy, systemIndex)`: a star system as a scene (plan §5 R4). Pure and deterministic; every draw
 * comes from `createRng(deriveSeed(galaxy.seed, 'layout', index, <part>))`, so parts never disturb each other.
 * Load-time code (≤ 5 ms per system); it allocates freely.
 */
import { createRng, range } from '../../core/rng'
import { loadGalaxyConfig } from '../../data/galaxyConfig'
import { deriveSeed } from '../seed'
import type { Galaxy } from '../types'
import { defaultFactionsConfig, defaultReachSystemsConfig, type FactionsConfig, type ReachSystemsConfig } from './config'
import { dangerOf } from './danger'
import { distance, dot, galaxyToWorld, length, negZ, normalize, scale, segmentDistance, shellGap, sub } from './frame'
import { buildHomeCluster, defaultHomeInputs, homeFieldIndex, type HomeInputs } from './homeCluster'
import { placeLanes } from './lanes'
import { placePois } from './pois'
import { placeRelays } from './relays'
import { ownerOf, placeStations } from './stations'
import type { BodySite, FieldSite, HomeCluster, PoiSite, StarSite, StationSite, SystemLayout, Vec3T } from './types'

const DEG = Math.PI / 180

export interface LayoutOptions {
  systems?: ReachSystemsConfig
  factions?: FactionsConfig
  /** The system radius (m); default `galaxy.json` `systemRadiusM`. */
  systemRadiusM?: number
  /** The Harrow's pose and seed; default `field.json` and `world.json`. */
  home?: HomeInputs
}

let cachedRadius: number | null = null

function defaultSystemRadius(): number {
  cachedRadius ??= loadGalaxyConfig().systemRadiusM
  return cachedRadius
}

/** Everything M3 places in one system, in the system frame. Throws for an index outside the galaxy. */
export function buildSystemLayout(galaxy: Galaxy, systemIndex: number, opts: LayoutOptions = {}): SystemLayout {
  const system = galaxy.systems[systemIndex]
  if (system === undefined) throw new Error(`layout: no system ${systemIndex}`)
  const cfg = opts.systems ?? defaultReachSystemsConfig()
  const factions = opts.factions ?? defaultFactionsConfig()
  const seedOf = (...part: (string | number)[]): number => deriveSeed(galaxy.seed, 'layout', systemIndex, ...part)
  const rngOf = (part: string): ReturnType<typeof createRng> => createRng(seedOf(part))

  const star: StarSite = {
    class: system.star.class,
    color: system.star.color,
    luminosity: system.star.luminosity,
    radiusM: cfg.stars.radiusM[system.star.class],
    seed: seedOf('star'),
  }

  const bodyRng = rngOf('bodies')
  const bodies = system.bodies.map((b, index): BodySite => {
    const r = b.orbitRadiusM
    return {
      id: b.id,
      index,
      kind: b.kind,
      position: galaxyToWorld([r * Math.cos(b.angleRad), r * Math.sin(b.angleRad), b.heightM ?? 0]),
      radiusM: b.radiusM,
      shellM: b.radiusM * cfg.bodies.shellFactor + cfg.bodies.shellExtraM,
      rings: b.rings,
      rotationPeriodS: b.rotationPeriodS,
      tiltRad: range(bodyRng, -cfg.bodies.tiltDeg, cfg.bodies.tiltDeg) * DEG,
      seed: seedOf('body', index),
    }
  })

  const isHome = system.anchor === 'home-gate'
  const homeField = isHome ? homeFieldIndex(system.fields, cfg) : -1
  const fields = system.fields.map(
    (f, index): FieldSite => ({
      id: f.id,
      index,
      kind: f.kind,
      center: galaxyToWorld(f.center),
      radiusM: f.radiusM,
      oreWeights: { ...f.oreWeights },
      seed: seedOf('field', index),
      authored: index === homeField ? 'm1-home' : null,
      // MC-a5a: the M8 mix and grade ride along only where the galaxy set them (absent stays absent).
      ...(f.mineralWeights === undefined ? {} : { mineralWeights: { ...f.mineralWeights } }),
      ...(f.grade === undefined ? {} : { grade: f.grade }),
    }),
  )

  const pois: PoiSite[] = placePois(galaxy.seed, system, bodies, star, cfg, rngOf('pois'))
  let home: HomeCluster | null = null
  let clusterStations: StationSite[] = []
  if (isHome) {
    const hc = buildHomeCluster(galaxy.seed, system, fields, pois.length, cfg, factions, opts.home ?? defaultHomeInputs())
    home = hc.home
    clusterStations = hc.stations
    pois.push(hc.harrow)
  }

  const generated = placeStations(
    { galaxySeed: galaxy.seed, system, star, bodies, fields, existing: clusterStations, cfg, factions },
    rngOf('stations'),
  )
  const stations = [...clusterStations, ...generated]
  const owner = ownerOf(stations, system.region, factions)
  const relays = placeRelays(galaxy, system, cfg, opts.systemRadiusM ?? defaultSystemRadius(), rngOf('relays'), bodies)
  const { lanes, rings } = placeLanes(
    { systemId: system.id, region: system.region, archetype: system.archetype, anchor: system.anchor, owner, star, bodies, stations, cfg },
    rngOf('lanes'),
  )

  return {
    systemId: system.id,
    systemIndex,
    name: system.name,
    region: system.region,
    archetype: system.archetype,
    anchor: system.anchor,
    seed: seedOf(),
    star,
    bodies,
    fields,
    stations,
    relays,
    lanes,
    laneRings: rings,
    pois,
    owner,
    danger: dangerOf(system, cfg),
    gateLandmark: isHome,
    home,
  }
}

// ---------------------------------------------------------------------------------------------
// validateLayout
// ---------------------------------------------------------------------------------------------

/** Tolerances for float64 checks on values up to about 5e10 m. */
const UNIT_EPS = 1e-9
const DIR_EPS = 1e-6
const POS_EPS_M = 1e-3

function finite(v: readonly number[]): boolean {
  return v.every((x) => Number.isFinite(x))
}

function unitQuat(q: readonly number[]): boolean {
  return finite(q) && Math.abs(Math.hypot(q[0], q[1], q[2], q[3]) - 1) < DIR_EPS
}

function sameDir(a: Vec3T, b: Vec3T): boolean {
  return dot(normalize(a), normalize(b)) > 1 - DIR_EPS
}

function uniqueIds(what: string, ids: readonly string[], out: string[]): void {
  if (new Set(ids).size !== ids.length) out.push(`${what}: duplicate ids`)
}

/**
 * Checks the layout's invariants and returns one message per problem (empty when it is sound): unique ids and
 * station names, indices, finite positions and unit rotations, station separation and clearance, relay frames, lane
 * geometry and ring clearance, POI clearance and the home cluster.
 */
export function validateLayout(layout: SystemLayout, cfg: ReachSystemsConfig = defaultReachSystemsConfig()): string[] {
  const out: string[] = []
  const { star, bodies, stations, relays, lanes, laneRings, pois } = layout
  const starClear = cfg.pois.starClearanceFactor * star.radiusM

  uniqueIds('bodies', bodies.map((b) => b.id), out)
  uniqueIds('fields', layout.fields.map((f) => f.id), out)
  uniqueIds('stations', stations.map((s) => s.id), out)
  uniqueIds('relays', relays.map((r) => r.relayId), out)
  uniqueIds('lanes', lanes.map((l) => l.id), out)
  uniqueIds('laneRings', laneRings.map((r) => r.id), out)
  uniqueIds('pois', pois.map((p) => p.id), out)
  if (new Set(stations.map((s) => s.name)).size !== stations.length) out.push('stations: duplicate names')

  const indexed: [string, readonly { index: number }[]][] = [
    ['bodies', bodies],
    ['fields', layout.fields],
    ['stations', stations],
    ['lanes', lanes],
    ['laneRings', laneRings],
    ['pois', pois],
  ]
  for (const [what, list] of indexed) {
    list.forEach((e, i) => {
      if (e.index !== i) out.push(`${what}[${i}]: index ${e.index}`)
    })
  }

  for (const s of stations) {
    if (!finite(s.position) || !unitQuat(s.rotation)) out.push(`${s.id}: non-finite position or non-unit rotation`)
    if (shellGap(s.position, bodies) <= 0) out.push(`${s.id}: inside a body shell`)
    if (length(s.position) < starClear) out.push(`${s.id}: within ${cfg.pois.starClearanceFactor} star radii`)
  }
  for (let i = 0; i < stations.length; i++) {
    for (let j = i + 1; j < stations.length; j++) {
      const a = stations[i]
      const b = stations[j]
      const cluster = a.anchor.kind === 'cluster' && b.anchor.kind === 'cluster'
      if (!cluster && distance(a.position, b.position) < cfg.placement.minSeparationM) out.push(`${a.id}–${b.id}: closer than minSeparationM`)
    }
  }

  for (const r of relays) {
    if (!finite(r.position) || !unitQuat(r.rotation)) out.push(`${r.relayId}: non-finite position or non-unit rotation`)
    if (Math.abs(length(r.inward) - 1) > UNIT_EPS * 1e3 || !sameDir(r.inward, scale(r.position, -1))) out.push(`${r.relayId}: inward is not −position`)
    if (!sameDir(negZ(r.rotation), scale(r.inward, -1))) out.push(`${r.relayId}: −Z is not outward`)
    if (shellGap(r.position, bodies) < cfg.relays.shellClearanceM - POS_EPS_M) out.push(`${r.relayId}: within shellClearanceM of a shell`)
  }

  lanes.forEach((lane) => {
    if (lane.ringIndices.length < cfg.lanes.ringsPerLane[0] || lane.ringIndices.length > cfg.lanes.ringsPerLane[1]) {
      out.push(`${lane.id}: ${lane.ringIndices.length} rings`)
    }
    if (!sameDir(lane.dir, sub(lane.to.position, lane.from.position))) out.push(`${lane.id}: dir is not from → to`)
    const len = distance(lane.from.position, lane.to.position)
    lane.ringIndices.forEach((ri, k) => {
      const ring = ri >= 0 && ri < laneRings.length ? laneRings[ri] : undefined
      if (ring === undefined || ring.laneIndex !== lane.index || ring.order !== k) {
        out.push(`${lane.id}: ring ${ri} does not belong at order ${k}`)
        return
      }
      if (segmentDistance(ring.position, lane.from.position, lane.to.position) > POS_EPS_M + len * 1e-12) out.push(`${ring.id}: off the lane line`)
      if (shellGap(ring.position, bodies) < cfg.lanes.shellClearanceM) out.push(`${ring.id}: within shellClearanceM of a shell`)
      if (!unitQuat(ring.rotation) || !sameDir(negZ(ring.rotation), lane.dir)) out.push(`${ring.id}: −Z is not the lane direction`)
    })
  })

  for (const p of pois) {
    if (!finite(p.position) || !unitQuat(p.rotation)) out.push(`${p.id}: non-finite position or non-unit rotation`)
    if (p.id === layout.home?.harrowPoiId) continue // the Harrow keeps M1's pose inside the home cluster
    if (shellGap(p.position, bodies) < cfg.pois.clearanceM - POS_EPS_M) out.push(`${p.id}: within clearanceM of a shell`)
    if (length(p.position) < starClear - POS_EPS_M) out.push(`${p.id}: within ${cfg.pois.starClearanceFactor} star radii`)
  }

  const home = layout.home
  if (home !== null) {
    if (!layout.fields.some((f) => f.id === home.fieldId && f.authored === 'm1-home')) out.push('home: its field is not authored m1-home')
    if (!pois.some((p) => p.id === home.harrowPoiId)) out.push('home: the Harrow POI is missing')
    if (Math.abs(length(home.spawnFacing) - 1) > DIR_EPS) out.push('home: spawnFacing is not a unit vector')
  }
  return out
}
